// Professional Seller Dashboard Container
import React from 'react';
import { Link } from 'react-router-dom';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';
import EarningsOverview from './EarningsOverview';
import QuickActions from './QuickActions';
import RecentSales from './RecentSales';

const DashboardContainer = ({ 
  availableBalance, 
  pendingBalance, 
  totalSales, 
  activeListings, 
  recentSales 
}) => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Calculate average earnings per SOLD listing, not active listings
  const soldItemsCount = recentSales ? recentSales.length : 0;
  const avgEarningsPerListing = soldItemsCount > 0 ? (totalSales / soldItemsCount).toFixed(2) : '0.00';
  const accountCompleteness = Math.round(
    ((activeListings > 0 ? 1 : 0) + 
     (totalSales > 0 ? 1 : 0) + 
     (availableBalance + pendingBalance > 0 ? 1 : 0)) / 3 * 100
  );
  const lastActivityStatus = activeListings > 0 ? 'Recently Active' : 'Inactive';
  const sellerLevel = totalSales === 0 ? 'New Seller' : 
                     totalSales < 100 ? 'Bronze' : 
                     totalSales < 500 ? 'Silver' : 'Gold';

  return (
    <div style={modernStyles.container}>
      {/* Professional Header */}
      <div style={modernStyles.headerSection}>
        <div style={modernStyles.headerContent}>
          <div style={modernStyles.titleSection}>
            <div style={modernStyles.breadcrumb}>
              <Link to="/market" style={modernStyles.breadcrumbLink}>Market</Link>
              <svg style={modernStyles.breadcrumbIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"/>
              </svg>
              <span style={modernStyles.breadcrumbCurrent}>Seller Dashboard</span>
            </div>
            <h1 style={modernStyles.pageTitle}>Dashboard</h1>
            <p style={modernStyles.dateText}>{currentDate}</p>
          </div>
          
          <div style={modernStyles.headerActions}>
            <div style={modernStyles.statusCard}>
              <div style={modernStyles.statusIcon}>
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                </svg>
              </div>
              <div style={modernStyles.statusInfo}>
                <span style={modernStyles.statusTitle}>Store Status</span>
                <span style={modernStyles.statusValue}>Active</span>
              </div>
            </div>
            
            <Link to="/add-listing" style={modernStyles.primaryAction}>
              <svg style={modernStyles.actionIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              <span>New Listing</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Dashboard Content Grid */}
      <div style={modernStyles.dashboardGrid}>
        <div style={modernStyles.mainContent}>
          <EarningsOverview 
            availableBalance={availableBalance}
            pendingBalance={pendingBalance}
            totalSales={totalSales}
            activeListings={activeListings}
          />

          <RecentSales sales={recentSales} />
        </div>

        <div style={modernStyles.sidebar}>
          <QuickActions />
          
          {/* Performance Summary */}
          <div style={modernStyles.performanceCard}>
            <h3 style={modernStyles.cardTitle}>
              <svg style={modernStyles.titleIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
              </svg>
              Performance Insights
            </h3>
            <div style={modernStyles.insightsList}>
              <div style={modernStyles.insightItem}>
                <div style={modernStyles.insightIcon}>
                  <svg fill={COLORS.primary} viewBox="0 0 24 24">
                    <path d="M12,12A3,3 0 0,0 15,9A3,3 0 0,0 12,6A3,3 0 0,0 9,9A3,3 0 0,0 12,12M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                  </svg>
                </div>
                <div style={modernStyles.insightContent}>
                  <span style={modernStyles.insightText}>Seller Level</span>
                  <span style={modernStyles.insightValue}>{sellerLevel}</span>
                </div>
              </div>
              
              <div style={modernStyles.insightItem}>
                <div style={modernStyles.insightIcon}>
                  <svg fill={COLORS.primary} viewBox="0 0 24 24">
                    <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
                  </svg>
                </div>
                <div style={modernStyles.insightContent}>
                  <span style={modernStyles.insightText}>Avg Per Listing</span>
                  <span style={modernStyles.insightValue}>${avgEarningsPerListing}</span>
                </div>
              </div>
              
              <div style={modernStyles.insightItem}>
                <div style={modernStyles.insightIcon}>
                  <svg fill={COLORS.primary} viewBox="0 0 24 24">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.5,8L11,13.5L7.5,10L6,11.5L11,16.5Z"/>
                  </svg>
                </div>
                <div style={modernStyles.insightContent}>
                  <span style={modernStyles.insightText}>Profile Status</span>
                  <span style={modernStyles.insightValue}>{accountCompleteness}% Complete</span>
                </div>
              </div>
              
              <div style={modernStyles.insightItem}>
                <div style={modernStyles.insightIcon}>
                  <svg fill={COLORS.primary} viewBox="0 0 24 24">
                    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                  </svg>
                </div>
                <div style={modernStyles.insightContent}>
                  <span style={modernStyles.insightText}>Activity Status</span>
                  <span style={modernStyles.insightValue}>{lastActivityStatus}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const modernStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
  },
  
  // Professional Header Section
  headerSection: {
    backgroundColor: COLORS.white,
    borderBottom: `1px solid ${COLORS.border}`,
    padding: `${LAYOUT.spacing.xl} 0`,
  },
  
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: `0 ${LAYOUT.spacing.xl}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.lg,
    flexWrap: 'wrap',
  },
  
  titleSection: {
    flex: 1,
    minWidth: '300px',
  },
  
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
    marginBottom: LAYOUT.spacing.sm,
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
  },
  
  breadcrumbLink: {
    color: COLORS.primary,
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'color 0.2s ease',
  },
  
  breadcrumbIcon: {
    width: '16px',
    height: '16px',
    color: COLORS.text.muted,
  },
  
  breadcrumbCurrent: {
    color: COLORS.text.primary,
    fontWeight: '600',
  },
  
  pageTitle: {
    ...TYPOGRAPHY.heading1,
    fontSize: '2.5rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.xs,
    lineHeight: '1.2',
  },
  
  dateText: {
    fontSize: '1rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },
  
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.lg,
    flexWrap: 'wrap',
  },
  
  statusCard: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    backgroundColor: COLORS.grayLight,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
  },
  
  statusIcon: {
    width: '24px',
    height: '24px',
    backgroundColor: 'rgba(206, 17, 38, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#CE1126',
  },
  
  statusInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  statusTitle: {
    fontSize: '0.85rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
    lineHeight: '1.2',
  },
  
  statusValue: {
    fontSize: '1.1rem',
    color: COLORS.text.primary,
    fontWeight: '700',
  },
  
  primaryAction: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(206, 17, 38, 0.2)',
  },
  
  actionIcon: {
    width: '20px',
    height: '20px',
  },
  
  // Dashboard Grid Layout
  dashboardGrid: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: LAYOUT.spacing.xl,
    display: 'grid',
    gridTemplateColumns: '1fr 350px',
    gap: LAYOUT.spacing.xl,
    alignItems: 'start',
  },
  
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xl,
  },
  
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.lg,
    position: 'sticky',
    top: LAYOUT.spacing.xl,
  },
  
  // Performance Card
  performanceCard: {
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    fontSize: '1.3rem',
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.lg,
  },
  
  titleIcon: {
    width: '20px',
    height: '20px',
    color: COLORS.primary,
  },
  
  insightsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
  },
  
  insightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    padding: LAYOUT.spacing.md,
    borderRadius: LAYOUT.borderRadius.lg,
    backgroundColor: COLORS.grayLight,
    border: `1px solid ${COLORS.border}`,
  },
  
  insightIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${COLORS.border}`,
  },
  
  insightContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  
  insightText: {
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },
  
  insightValue: {
    fontSize: '1.1rem',
    color: COLORS.text.primary,
    fontWeight: '700',
  },
  
  // Responsive Design
  '@media (max-width: 1024px)': {
    dashboardGrid: {
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.lg,
    },
    
    sidebar: {
      position: 'static',
      order: -1,
    },
    
    headerContent: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: LAYOUT.spacing.md,
    },
    
    headerActions: {
      width: '100%',
      justifyContent: 'space-between',
    },
  },
};



// Add dashboard animations
const dashboardAnimations = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  .dashboard-hero {
    animation: fadeInUp 0.6s ease-out;
  }
  
  .dashboard-card {
    animation: fadeInUp 0.6s ease-out;
  }
  
  .dashboard-card:nth-child(1) { animation-delay: 0.1s; }
  .dashboard-card:nth-child(2) { animation-delay: 0.2s; }
  .dashboard-card:nth-child(3) { animation-delay: 0.3s; }
  .dashboard-card:nth-child(4) { animation-delay: 0.4s; }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .dashboard-card:hover .action-arrow {
    transform: translateX(4px);
  }
`;

// Inject dashboard styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('dashboard-animations');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'dashboard-animations';
    style.textContent = dashboardAnimations;
    document.head.appendChild(style);
  }
}

export default DashboardContainer;